import React, { useEffect, useMemo, useState } from 'react';
import Tree from 'react-d3-tree';
import { fetchWhereUsedTreeData } from '../../../../services/Inventory/InventoryDetailServices';
import StockBar from '../../../../Components/DetailsPageUpperLayer/StockBar';
import { getProjectionDotColor } from '../../../../utils/colorUtils';
import { Typography } from '@mui/material';

/**
 * @param {string} siteCode - The site identifier
 * @param {string} item - The item identifier
 * @param {function} onNodeClick - Callback function returning { id, itemName }
 */
const WhereUsedRenderChart = ({ siteCode, item, onNodeClick, selectedNodeId }) => {
  const [rows, setRows] = useState([]);
  // Use a status string to precisely track the API lifecycle
  const [status, setStatus] = useState('loading'); // loading, success, error, empty

  useEffect(() => {
    let isCurrent = true;
    setStatus('loading');

    const fetchData = async () => {
      try {
        const apiResponse = await fetchWhereUsedTreeData(item, siteCode);

        if (!isCurrent) return;

        // Check if the response exists and contains data (200 OK logic)
        const data = apiResponse?.data ?? apiResponse;

        if (data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)) {
          setRows(Array.isArray(data) ? data : [data]);
          setStatus('success');
        } else {
          // Response was successful but returned no data
          setStatus('empty');
        }
      } catch (err) {
        console.error('Error fetching where-used tree data:', err);
        if (!isCurrent) return;
        setRows([]);
        setStatus('error');
      }
    };

    fetchData();
    return () => {
      isCurrent = false;
    };
  }, [siteCode, item]);

  const formatValue = (val) => {
    if (val === undefined || val === null || Number.isNaN(Number(val))) return '0';
    return Number(val).toLocaleString();
  };

  const objectToWeekArray = (obj) => {
    if (!obj) return Array(6).fill(0);
    return ['week1', 'week2', 'week3', 'week4', 'week5', 'week6'].map(k => Number(obj[k]) || 0);
  };

  const treeData = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const transform = (node, level = 0) => ({
      ...node,
      name: node.itemName || node.label || node.id,
      // only allow children at level 0 (root)
      children: level === 0 ? (node.children || []).map(child => transform(child, level + 1)) : []
    });
    return rows.map(node => transform(node, 0));
  }, [rows]);

  const treeWidth = Math.max(1600, (treeData?.[0]?.children?.length || 1) * 260);

  const renderCustomNode = ({ nodeDatum, toggleNode }) => {
    const projected = objectToWeekArray(nodeDatum.projection6W);
    const min_stock = objectToWeekArray(nodeDatum.minStock6W);
    const max_stock = objectToWeekArray(nodeDatum.maxStock6W);
    const isParent = nodeDatum.__rd3t?.depth === 0;
    const isSelected = isParent || (!isParent && nodeDatum.id === selectedNodeId);

    const handleNodeClick = (e) => {
      toggleNode();
      if (onNodeClick) {
        onNodeClick({
          id: nodeDatum.id,
          itemName: nodeDatum.itemName || nodeDatum.label || nodeDatum.id
        });
      }
    };

    return (
      <g>
        <foreignObject width="220" height="180" x="-110" y="-70">
          <div
            onClick={handleNodeClick}
            style={{
              padding: '12px',
              background: isSelected ? '#bab8b8ff' : '#ffffff',
              borderRadius: '10px',
              boxShadow: isSelected
                ? '0 6px 14px rgba(0,0,0,0.15)'
                : '0 4px 12px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
            }}
          >
            <div style={{ fontSize: '9px', color: isSelected ? '#fff' : '#747272ff', marginBottom: '2px' }}>
              ID: {nodeDatum.id}
            </div>

            <div style={{
              fontSize: '11px',
              fontWeight: 600,
              marginBottom: '10px',
              height: '32px',
              overflow: 'hidden',
              lineHeight: '1'
            }}>
              {nodeDatum.itemName || nodeDatum.label || nodeDatum.id}
            </div>

            <StockBar
              stock={Number(nodeDatum.stock?.current) || 0}
              min_stock={Number(nodeDatum.stock?.min) || 0}
              max_stock={Number(nodeDatum.stock?.max) || 0}
              projected_stock={Number(nodeDatum.stock?.projected) || 0}
              showLabels={true}
              lebelColor="#000000"
              source="details"
            />

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '15px',
              borderTop: isSelected ? '1px solid #fff' : '1px solid #bab8b8ff',
              paddingTop: '10px'
            }}>
              {projected.map((value, idx) => {
                const color = getProjectionDotColor(value, min_stock[idx], max_stock[idx]);
                return (
                  <div
                    key={idx}
                    title={`Week ${idx + 1}: ${formatValue(value)}`}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: color,
                      border: '1px solid #eee'
                    }}
                  />
                );
              })}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };

  // --- RENDER LOGIC ---

  // 1. Loading state (API is in progress)
  if (status === 'loading') {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Segoe UI' }}>
        Loading Hierarchy Chart...
      </div>
    );
  }

  // 2. Error state (API returned 4xx, 5xx, or network error)
  if (status === 'error') {
    return (
      <div style={{ padding: '40px', color: '#d32f2f', textAlign: 'center', fontFamily: 'Segoe UI' }}>
        <strong>Error:</strong> Failed to load hierarchy data. Please try again later.
      </div>
    );
  }

  // 3. Empty state (API returned 200 but data is empty)
  if (status === 'empty') {
    return (
      <div style={{ padding: '40px', color: '#666', textAlign: 'center', fontFamily: 'Segoe UI' }}>
        No where-used hierarchy found for this item.
      </div>
    );
  }

  // 4. Success state (Only show chart when status is 'success')
  return (
    <div style={{
      width: '100%',
      overflowX: 'auto',
      overflowY: 'hidden',
      backgroundColor: '#F2F3F4'
    }}>
      <div
        style={{
          width: treeWidth,
          height: 380
        }}
      >
        <Tree
          data={treeData}
          renderCustomNodeElement={renderCustomNode}
          orientation="vertical"
          pathFunc="step"
          nodeSize={{ x: 250, y: 180 }}
          separation={{ siblings: 0.95, nonSiblings: 0.9 }}
          translate={{ x: treeWidth / 2, y: 120 }}
        />
      </div>
    </div>
  );
};

export default WhereUsedRenderChart;